import { useState, useCallback, useRef } from 'react'
import type { JSX } from 'react'

import { GlobeIcon, UnstyledButton } from '@buffer-mono/popcorn'

import { useTruncatedText } from '~publish/hooks/useTruncatedText'
import { DocumentPreview } from '~publish/legacy/composer/composer/components/document-attachment/DocumentPreview'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseLinkedInLinks } from '~publish/legacy/utils/channels/linkedin/helpers'

import { LinkedInPostActions } from './LinkedInPostActions'
import { CircleAvatar } from '../../../Common/components/CircleAvatar'
import { useExpandablePreviewText } from '../../../Common/hooks/useExpandablePreviewText'
import useLinkPreviewEffect from '../../../Common/hooks/useLinkPreviewEffect'
import { ImagesGrid } from '../../../Common/ImagesGrid'
import { LinkPreview } from '../../../Common/LinkPreview'
import {
  AvatarSquare,
  DotsIconSmall,
  PostWrapper,
} from '../../../Common/styles'
import {
  removeLinkFromEndOfText,
  DEFAULT_AVATAR,
  getMediaProperties,
  gifAsImage,
} from '../../../Common/utils'
import { VideoPlayer } from '../../../Common/VideoPlayer'
import type { LinkedInContent, ProfilePreview } from '../../../types'
import { extendToLineEnd, formatImagesInRows } from '../../utils'

import styles from './Post.module.css'

const shortenUsername = (name: string): string => {
  if (name.length > 25) {
    return name.substring(0, 16) + '…' + name.substring(name.length - 5)
  }
  return name
}

const ProfileAvatar = ({
  avatar,
  username,
  serviceType,
  size,
}: {
  avatar: string
  username: string
  serviceType: string
  size: 'xsmall' | 'small' | 'mid' | 'medium' | 'large'
}): JSX.Element => {
  if (serviceType === 'page') {
    return (
      <AvatarSquare
        data-testid="avatar"
        src={avatar}
        alt={username}
        fallbackUrl={DEFAULT_AVATAR}
      />
    )
  }

  return (
    <CircleAvatar src={avatar || DEFAULT_AVATAR} alt={username} size={size} />
  )
}

const FirstCommentIndicator = (): JSX.Element => (
  <div className={styles.firstCommentIndicator}>1 comment</div>
)

const FirstComment = ({
  avatar,
  username,
  serviceType,
  commentText,
}: {
  avatar: string
  username: string
  serviceType: string
  commentText: string
}): JSX.Element => {
  const { textToDisplay, isTruncated, isExpanded, expand } =
    useExpandablePreviewText({
      channel: 'linkedin',
      text: commentText,
      customCharLimit: 500,
    })
  const usernameToDisplay = shortenUsername(username)
  return (
    <div className={styles.singleCommentSection}>
      <ProfileAvatar
        avatar={avatar}
        username={username}
        serviceType={serviceType}
        size="small"
      />
      <div className={styles.commentWrapper}>
        <div className={styles.commentHeader}>
          <span className={styles.commentUsernameWrapper}>
            <span translate="no">{usernameToDisplay}</span>{' '}
            <span className={styles.authorTag}>Author</span>
          </span>
          <DotsIconSmall />
        </div>
        <div>
          <LinkifiedText
            className={styles.linkifiedCommentText}
            links={parseLinkedInLinks(textToDisplay, [])}
            size="mini"
            whitespace="pre-wrap"
            newTab
          >
            {textToDisplay}
          </LinkifiedText>
          {isTruncated && !isExpanded && (
            <UnstyledButton onClick={expand} className={styles.seeMoreButton}>
              {' '}
              ...see more
            </UnstyledButton>
          )}
        </div>
      </div>
    </div>
  )
}

const LINKEDIN_LINE_CLAMP = 3

const LinkedInPost = ({
  content,
  profile,
}: {
  content: LinkedInContent
  profile: ProfilePreview
}): JSX.Element => {
  const textRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const expand = useCallback(() => {
    setIsExpanded(true)
  }, [])

  const { avatar, username, serviceType, displayName } = profile
  const { text, images, gif, link: linkFromPost, video, annotations } = content
  const commentText = content?.channelData?.linkedin?.comment_text
  const [linkPreview, linkText, scrapedData] = useLinkPreviewEffect({
    linkFromPost,
    text,
    checkValidUrl: false,
    compareWithScraper: true,
    useScraperData: false,
  })
  const hasScrapedData = !!(scrapedData && scrapedData.thumbnail)
  const hasLinkData = !!(linkPreview && linkPreview.thumbnail)
  const thumbnailEdited =
    hasScrapedData &&
    hasLinkData &&
    scrapedData.thumbnail?.url !== linkPreview.thumbnail?.originalUrl

  const { hasImages, hasVideo, hasLink, hasGif } = getMediaProperties(
    content,
    linkPreview,
  )
  const hasDocument = !!content.document?.id

  // Remove link from end of text when there's a link preview (to avoid showing URL twice)
  const processedText =
    hasLink && linkText ? removeLinkFromEndOfText(text ?? '', linkText) : text

  // LinkedIn uses visual line clamping (useTruncatedText) instead of character-based
  // truncation to match the native LinkedIn experience more closely
  const [truncatedText, didTruncate] = useTruncatedText({
    text: processedText ?? '',
    lineClamp: LINKEDIN_LINE_CLAMP,
    container: textRef,
    expansionText: '...more',
    expansionTextBufferMultiplier: 1,
  })

  // Check if container has valid width (needed for proper truncation calculation)
  const hasValidContainerWidth = (textRef.current?.offsetWidth ?? 0) > 0

  // Get text to display - extend truncated text to complete current line
  // Fall back to original text if container has no width (e.g. in tests) or truncatedText is empty
  const displayText = isExpanded
    ? processedText
    : hasValidContainerWidth && truncatedText
      ? extendToLineEnd(truncatedText.trimEnd(), processedText, {
          maxExtraChars: 12,
        })
      : processedText

  // Only show "...more" button if we have valid truncation
  const showMoreButton = didTruncate && hasValidContainerWidth && !isExpanded

  const allImages = images?.length ? images : gif ? [gifAsImage(gif)] : []
  const title = content.document?.title || 'Untitled document'
  const usernameToDisplay = displayName || username

  return (
    <PostWrapper>
      <div className={styles.header}>
        <ProfileAvatar
          avatar={avatar}
          username={username}
          serviceType={serviceType}
          size="medium"
        />
        <div className={styles.usernameWrapper}>
          <span className={styles.username} translate="no">
            {usernameToDisplay}
          </span>
          <span className={styles.formattedUsername}>
            {`1h · `}
            <GlobeIcon />
          </span>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.content}>
          <div ref={textRef} className={styles.truncatedTextWrapper}>
            <LinkifiedText
              links={parseLinkedInLinks(displayText ?? '', annotations)}
              size="extra-small"
              whitespace="pre-wrap"
              newTab
            >
              {displayText}
            </LinkifiedText>
            {showMoreButton && (
              <UnstyledButton onClick={expand} className={styles.seeMoreButton}>
                ...more
              </UnstyledButton>
            )}
          </div>
        </div>
      </div>
      {(hasImages || hasGif) && (
        <ImagesGrid
          className={styles.imagesGrid}
          images={allImages}
          isRowFormat={allImages.length !== 2}
          formatImages={formatImagesInRows}
        />
      )}
      {hasDocument &&
        content?.document?.url && (
          // width is .previewViewport minus minus .page padding
          <DocumentPreview
            title={title}
            url={content.document.url}
            width={290}
          />
        )}
      {hasVideo && video && <VideoPlayer video={video} />}
      {hasLink && linkPreview && (
        <LinkPreview
          className={styles.linkPreview}
          link={linkPreview}
          titleOnTop
          imageCover
          showEmptyThumbnail
          clickable={false}
          showCompact={thumbnailEdited}
        />
      )}
      {commentText && <FirstCommentIndicator />}
      {!hasDocument && <div className={styles.separator} />}
      {hasDocument && commentText && <div className={styles.separator} />}
      <LinkedInPostActions avatar={avatar} username={username} />
      {commentText && (
        <FirstComment
          avatar={avatar}
          username={username}
          serviceType={serviceType}
          commentText={commentText}
        />
      )}
    </PostWrapper>
  )
}

export default LinkedInPost
